import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#3B4A4F';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-2613" data-mp="1ma7o28x1qxdg">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-5 leading-[1.2]" data-id="element-2614" data-mp="1r8hhadz7nnwf">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#3A3A33] leading-relaxed" data-id="element-2615" data-mp="1w956qv7sjwoj">
        {children}
      </div>
    </motion.section>;
}
export function Privacy() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2616" data-mp="37lzbm1pi7mz3">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#E5E5E5]" data-id="element-2617" data-mp="1hm77kcviaz7w">
        <div className="max-w-3xl mx-auto" data-id="element-2618" data-mp="1djkbu0vpsu5f">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2619" data-mp="1dkvg01t2o3x2">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-2620" data-mp="18jpm40noealy">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.05] mb-5" data-id="element-2621" data-mp="twcy85aebsse">
              Privacy Policy
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#5A5A50]" data-id="element-2622" data-mp="kw7rlxd10yfa">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-2623" data-mp="aya79kyaflry">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-2624" data-mp="19mq21tn13vz">
          <motion.p variants={fadeUp} className="text-[15px] text-[#3A3A33] leading-relaxed mb-12" data-id="element-2625" data-mp="3ofnomva2dwd7">
            Structured Nutrition ("we," "us," or "our") respects your privacy.
            This Privacy Policy explains what information we collect, how we use
            it, and the choices you have. By using our website and services, you
            agree to the practices described below.
          </motion.p>

          <Section title="Information We Collect" data-id="element-2626" data-mp="f9e94gz0hagu">
            <p data-id="element-2627" data-mp="f7ut8rh9p1fl">We collect information you provide directly to us, including:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2628" data-mp="12xxiz0xplvb1">
              <li data-id="element-2629" data-mp="17pglcnfal9ao">
                <strong data-id="element-2630" data-mp="17pyqyanugzv9">Contact details:</strong> Name, email address, and phone
                number when you submit our contact form or sign up for an
                account.
              </li>
              <li data-id="element-2631" data-mp="2nhkfif2d5ctl">
                <strong data-id="element-2632" data-mp="2ng9euo47sl6p">Account information:</strong> Login credentials and
                profile data managed through our authentication provider
                (Firebase).
              </li>
              <li data-id="element-2633" data-mp="2x7vo4ewzsa6t">
                <strong data-id="element-2634" data-mp="2x7cermlequ4e">Payment information:</strong> Processed securely through
                Stripe. We do not store full credit card numbers on our servers.
              </li>
              <li data-id="element-2635" data-mp="24j874vja06z2">
                <strong data-id="element-2636" data-mp="24jq1tnqaid4v">Communications:</strong> Messages you send us through
                forms, email, or social channels.
              </li>
              <li data-id="element-2637" data-mp="2egmc9yzr19c2">
                <strong data-id="element-2638" data-mp="2eg33p8vznxap">Usage data:</strong> Pages visited, time on site, and
                device/browser information collected through cookies and
                analytics tools.
              </li>
            </ul>
          </Section>

          <Section title="How We Use Your Information" data-id="element-2639" data-mp="3pv8aanz8qfzl">
            <p data-id="element-2640" data-mp="1zf60evfulm17">We use the information we collect to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2641" data-mp="1zgh6j1wrn5vh">
              <li data-id="element-2642" data-mp="24ea97kyv1hr0">Fulfill orders and improve our products and store</li>
              <li data-id="element-2643" data-mp="28ekmzjsw9cgf">Process payments and deliver purchases</li>
              <li data-id="element-2644" data-mp="28hf9rhzdr9d0">Respond to inquiries and provide customer support</li>
              <li data-id="element-2645" data-mp="2dfqifpdxvdtn">
                Send updates, newsletters, or marketing (only if you opt in)
              </li>
              <li data-id="element-2646" data-mp="2ib5vif44h5p0">
                Comply with legal obligations and protect against fraud or abuse
              </li>
            </ul>
          </Section>

          <Section title="Third-Party Services" data-id="element-2647" data-mp="2s2thi0hwtuj3">
            <p data-id="element-2648" data-mp="2s566gxm3555n">
              We share information with trusted third-party providers who help
              us operate our business:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2649" data-mp="314jaharfx1iz">
              <li data-id="element-2650" data-mp="362uj5i6015zm">
                <strong data-id="element-2651" data-mp="362b7gj6xzdu1">Stripe</strong> — payment processing
              </li>
              <li data-id="element-2652" data-mp="3g6rpnj7w9710">
                <strong data-id="element-2653" data-mp="3g7az0bjhan3f">Firebase (Google)</strong> — authentication and database
              </li>
              <li data-id="element-2654" data-mp="3b75bn7k8rmr5">
                <strong data-id="element-2655" data-mp="3b6n9xvet8gbu">Formspree</strong> — contact form submissions
              </li>
              <li data-id="element-2656" data-mp="367tu3wc1wd8i">
                <strong data-id="element-2657" data-mp="369d9zltsom9r">Google Analytics / Google Tag Manager</strong> — site
                analytics
              </li>
              <li data-id="element-2658" data-mp="328tf6owp1uqs">
                <strong data-id="element-2659" data-mp="2rve8crar5pp">Meta Pixel</strong> — advertising and analytics
              </li>
            </ul>
            <p data-id="element-2660" data-mp="3ty66bjebww4w">
              These providers have their own privacy policies governing how they
              handle your data.
            </p>
          </Section>

          <Section title="Cookies and Tracking" data-id="element-2661" data-mp="3p5mw8weuoj4u">
            <p data-id="element-2662" data-mp="3l6e23qfwej8r">
              We use cookies and similar technologies to remember your
              preferences, keep you logged in, analyze site traffic, and measure
              advertising effectiveness. You can disable cookies in your browser
              settings, though some site features may stop working as a result.
            </p>
          </Section>

          <Section title="Your Rights" data-id="element-2663" data-mp="2b1l0i0ce7h3p">
            <p data-id="element-2664" data-mp="2fyup5dryjoqh">Depending on where you live, you may have the right to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2665" data-mp="219or3t4uw4k6">
              <li data-id="element-2666" data-mp="219f1i6nq3zf0">Access the personal information we hold about you</li>
              <li data-id="element-2667" data-mp="267qdamxkwjzv">Request that we correct or delete your information</li>
              <li data-id="element-2668" data-mp="1s8h78g33769m">Opt out of marketing communications at any time</li>
              <li data-id="element-2669" data-mp="1sbd564expuy2">
                Withdraw consent for processing where consent is the legal basis
              </li>
            </ul>
            <p data-id="element-2670" data-mp="1ibcl2g98rast">
              To exercise any of these rights, contact us using the details
              below.
            </p>
          </Section>

          <Section title="Data Security" data-id="element-2671" data-mp="1na5tvvdl2dmn">
            <p data-id="element-2672" data-mp="3coc4kkgqsp5d">
              We use reasonable administrative, technical, and physical
              safeguards to protect your information. However, no method of
              transmission over the internet is 100% secure, and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section title="Children's Privacy" data-id="element-2673" data-mp="112jox69wiy3m">
            <p data-id="element-2674" data-mp="10znpfdieo1d2">
              Our services are not directed to individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              you believe a child has provided us information, please contact us
              so we can delete it.
            </p>
          </Section>

          <Section title="Changes to This Policy" data-id="element-2675" data-mp="1kq0v8x7ngkq1">
            <p data-id="element-2676" data-mp="1kp7y8nyht9l3">
              We may update this Privacy Policy from time to time. When we do,
              we'll revise the "Last updated" date at the top of this page.
              Significant changes will be communicated via the site or email.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-2677" data-mp="4bril0crskf1">
            <p data-id="element-2678" data-mp="3vhiuaupsvuib">
              Questions about this Privacy Policy or your data? Reach out
              through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-2679" data-mp="39owr1yp2umvu">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#5A5A50] italic mt-16 pt-8 border-t border-[#E5E5E5]" data-id="element-2680" data-mp="3odsvllrxdhrp">
            This document is provided as a general template and does not
            constitute legal advice. We recommend reviewing it with an attorney
            before relying on it.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}